/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { switchMap, catchError, map, takeUntil, mergeMap, exhaustMap, tap, mapTo } from 'rxjs/operators';
import { of, from, EMPTY } from 'rxjs';
import { AxiosError } from 'axios';
import { downloadDataAsFile, NOTIFICATION_TYPES, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import {
    createCashboxesTasksErrorAction,
    createCashboxesTasksSuccessAction,
    fetchRoughCashboxesListPageErrorAction,
    fetchRoughCashboxesListPageSuccessAction,
    getCashboxesTasksStatusesErrorAction,
    getCashboxesTasksStatusesSuccessAction,
    ServiceActions,
    ServiceActionType,
    fetchDevicesListErrorAction,
    fetchDevicesListSuccessAction,
    fetchFactoryNumbersListErrorAction,
    fetchFactoryNumbersListSuccessAction,
    performDevicesOperationErrorAction,
    performDevicesOperationSuccessAction,
    uploadFileErrorAction,
    uploadFileSuccessAction,
    updateServiceCashboxesDataSuccessAction,
    updateServiceCashboxesDataErrorAction,
    fetchSuitableFwTargetVersionsSuccessAction,
    fetchSuitableFwTargetVersionsErrorAction,
    downloadCashBoxLogsSuccessAction,
    downloadCashBoxLogsErrorAction,
} from '../actions/serviceActions';
import { serviceClient } from '../api/service/serviceClient';
import {
    CreateCashBoxTaskResponse,
    RoughCashboxesListRequestParams,
    ServiceCashboxesListItem,
} from '../api/service/serviceClient.typings';

export const fetchRoughCashBoxesListEpic: Epic<ServiceActions> = (action$, state) => {
    return action$.pipe(
        ofType(
            ServiceActionType.FETCH_ROUGH_CASHBOXES_LIST_NEXT_PAGE,
            ServiceActionType.APPLY_ROUGH_CASHBOXES_LIST_FILTERS,
        ),
        switchMap(() => {
            const queryParams: RoughCashboxesListRequestParams = {
                ...state.value.serviceReducer.roughCashboxesListData.filterParams,
                limit: state.value.serviceReducer.roughCashboxesListData.limit,
                afterKeyTaxNumber: state.value.serviceReducer.roughCashboxesListData.afterKeyTaxNumber,
            };

            return from(serviceClient.getRoughCashboxesList(queryParams)).pipe(
                map((list) => fetchRoughCashboxesListPageSuccessAction(list)),

                catchError((err: AxiosError) =>
                    of(
                        fetchRoughCashboxesListPageErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashboxesListNotFound')),
                        ),
                    ),
                ),
                takeUntil(
                    action$.pipe(
                        ofType(ServiceActionType.CLOSE_ADD_CASHBOXES_DIALOG, ServiceActionType.EXIT_SERVICE_PAGE),
                    ),
                ),
            );
        }),
    );
};

export const createCashboxTasksEpic: Epic<ServiceActions> = (action$) => {
    return action$.pipe(
        ofType(ServiceActionType.CREATE_CASHBOXES_TASKS),
        switchMap((action) => {
            return from(serviceClient.createCashboxesTasks(action.payload.params)).pipe(
                map((response) => {
                    let responseWithTaskName: CreateCashBoxTaskResponse[] = [];
                    if (response && response.length > 0) {
                        responseWithTaskName = response.map((item, index) => {
                            return {
                                id: item.id,
                                cashBoxTaxNumber: item.cashBoxTaxNumber,
                                errorMessage: item.errorMessage,
                                taskName: action.payload.params[index].name,
                            };
                        });
                    }
                    return createCashboxesTasksSuccessAction({
                        taskType: action.payload.taskType,
                        response: responseWithTaskName,
                    });
                }),

                catchError((err: AxiosError) => {
                    return of(
                        createCashboxesTasksErrorAction(
                            parseError(err, i18n.t('ServiceScreen.errors.failedToCreateCashboxesTasks')),
                        ),
                    );
                }),

                takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
            );
        }),
    );
};

export const getCashboxesTasksStatusEpic: Epic<ServiceActions> = (action$, state) => {
    return action$.pipe(
        ofType(ServiceActionType.GET_CASHBOXES_TASKS_STATUSES),
        switchMap((action) => {
            const cashBoxTaxNumbers: string[] = [];
            if (state.value.serviceReducer.serviceCashboxesList.size !== 0) {
                state.value.serviceReducer.serviceCashboxesList.forEach((cashbox: ServiceCashboxesListItem) => {
                    cashBoxTaxNumbers.push(cashbox.taxNumber);
                });
            }
            return from(
                serviceClient.getCashboxesTasksStatuses({
                    cashBoxTaxNumbers,
                    name: action.payload,
                    latest: true,
                    limit: cashBoxTaxNumbers.length,
                    offset: 0,
                }),
            ).pipe(
                map((resp) => {
                    return getCashboxesTasksStatusesSuccessAction(resp);
                }),

                catchError((err: AxiosError) => {
                    return of(
                        getCashboxesTasksStatusesErrorAction(
                            parseError(err, i18n.t('ServiceScreen.errors.failedToGetCashboxesTasksStatuses')),
                        ),
                    );
                }),

                takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
            );
        }),
    );
};

export const updateCashboxesTasksStatusOnCreateTasksSuccessEpic: Epic<ServiceActions> = (action$, state) => {
    return action$.pipe(
        ofType(ServiceActionType.CREATE_CASHBOXES_TASKS_SUCCESS),
        switchMap((action) => {
            const rejectedTasks: CreateCashBoxTaskResponse[] = action.payload.response.filter(
                (item) => item.id === null,
            );
            let cashboxesWithRejectedTasks: string[] = [];
            if (rejectedTasks.length > 0) {
                cashboxesWithRejectedTasks = rejectedTasks.map((item) => item.cashBoxTaxNumber);
            }
            const cashBoxTaxNumbers: string[] = [];
            if (state.value.serviceReducer.serviceCashboxesList.size !== 0) {
                state.value.serviceReducer.serviceCashboxesList.forEach((cashbox: ServiceCashboxesListItem) => {
                    if (!cashboxesWithRejectedTasks.includes(cashbox.taxNumber))
                        cashBoxTaxNumbers.push(cashbox.taxNumber);
                });
            }
            if (cashBoxTaxNumbers.length > 0) {
                return from(
                    serviceClient.getCashboxesTasksStatuses({
                        cashBoxTaxNumbers,
                        name: action.payload.taskType,
                        latest: true,
                        limit: cashBoxTaxNumbers.length,
                        offset: 0,
                    }),
                ).pipe(
                    map((resp) => {
                        return getCashboxesTasksStatusesSuccessAction(resp);
                    }),

                    catchError((err: AxiosError) => {
                        return of(
                            getCashboxesTasksStatusesErrorAction(
                                parseError(err, i18n.t('ServiceScreen.errors.failedToGetCashboxesTasksStatuses')),
                            ),
                        );
                    }),

                    takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
                );
            } else return EMPTY;
        }),
    );
};

export const updateServiceCashBoxesListEpic: Epic<ServiceActions> = (action$, state) => {
    return action$.pipe(
        ofType(ServiceActionType.UPDATE_SERVICE_CASHBOXES_DATA, ServiceActionType.CREATE_CASHBOXES_TASKS_SUCCESS),
        switchMap(() => {
            const cashBoxTaxNumbers: string[] = [];
            if (state.value.serviceReducer.serviceCashboxesList.size !== 0) {
                state.value.serviceReducer.serviceCashboxesList.forEach((cashbox: ServiceCashboxesListItem) => {
                    cashBoxTaxNumbers.push(cashbox.taxNumber);
                });
            }
            return from(serviceClient.updateServiceCashboxesList(cashBoxTaxNumbers)).pipe(
                map((list) => updateServiceCashboxesDataSuccessAction(list)),

                catchError((err: AxiosError) =>
                    of(
                        updateServiceCashboxesDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashboxesListNotFound')),
                        ),
                    ),
                ),
                takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
            );
        }),
    );
};

export const fetchSuitableFwTargetVersionsEpic: Epic<ServiceActions> = (action$) => {
    return action$.pipe(
        ofType(ServiceActionType.FETCH_SUITABLE_FW_TARGET_VERSIONS),
        mergeMap((action) => {
            return from(serviceClient.getSuitableTargetVersions(action.payload)).pipe(
                map((resp) => {
                    return fetchSuitableFwTargetVersionsSuccessAction({
                        versions: [...resp.versions],
                        devicePlatform: action.payload.devicePlatform,
                    });
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchSuitableFwTargetVersionsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.getSuitableTargetVersionsFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
            );
        }),
    );
};

export const downloadCashBoxLogsZipFileEpic: Epic<ServiceActions> = (action$) => {
    return action$.pipe(
        ofType(ServiceActionType.DOWNLOAD_CASHBOX_LOGS),
        exhaustMap((action) => {
            const fileName = action.payload.fileName;

            return from(serviceClient.downloadCashBoxLogsFile(action.payload.logId)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),
                mapTo(downloadCashBoxLogsSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadCashBoxLogsErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
            );
        }),
    );
};

export const uploadInitialisationTabFileEpic: Epic<ServiceActions> = (action$) => {
    return action$.pipe(
        ofType(ServiceActionType.UPLOAD_FILE_START),
        switchMap((action) => {
            const { fileMetaData, operation } = action.payload;
            return from(
                serviceClient.uploadInitialisationFile({
                    file: fileMetaData.file,
                    operation,
                }),
            ).pipe(
                map(() =>
                    uploadFileSuccessAction(
                        {
                            type: NOTIFICATION_TYPES[1],
                            message: i18n.t('ServiceScreen.hints.operationSuccessful'),
                        },
                        fileMetaData,
                    ),
                ),

                catchError((err: AxiosError) =>
                    of(uploadFileErrorAction(parseError(err, i18n.t('errors.failedUploadFile'), true))),
                ),
                takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
            );
        }),
    );
};

export const searchFactoryNumbersListEpic: Epic<ServiceActions> = (action$) => {
    return action$.pipe(
        ofType(ServiceActionType.FETCH_FACTORY_NUMBER_LIST),
        switchMap((action) => {
            return from(serviceClient.searchFactoryNumbers(action.payload)).pipe(
                map((result) => fetchFactoryNumbersListSuccessAction(result)),

                catchError((err: AxiosError) =>
                    of(
                        fetchFactoryNumbersListErrorAction(
                            parseError(err, i18n.t('ServiceScreen.errors.failedToLoadFactoryNumbersList')),
                        ),
                    ),
                ),
                takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
            );
        }),
    );
};

export const fetchDevicesListEpic: Epic<ServiceActions> = (action$, state$) => {
    return action$.pipe(
        ofType(
            ServiceActionType.FETCH_DEVICES_LIST,
            ServiceActionType.PERFORM_DEVICES_OPERATION_SUCCESS,
            ServiceActionType.PERFORM_DEVICES_OPERATION_ERROR,
        ),
        switchMap(() => {
            const factoryNumbers = state$.value.serviceReducer.selectedFactoryNumbersList;

            return from(serviceClient.devicesList([...factoryNumbers])).pipe(
                map((result) => fetchDevicesListSuccessAction(result)),

                catchError((err: AxiosError) =>
                    of(
                        fetchDevicesListErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.devicesListNotFound')),
                        ),
                    ),
                ),
                takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
            );
        }),
    );
};

export const performDevicesOperation: Epic<ServiceActions> = (action$) => {
    return action$.pipe(
        ofType(ServiceActionType.PERFORM_DEVICES_OPERATION),
        switchMap((action) => {
            return from(serviceClient.performDeviceOperations(action.payload)).pipe(
                map(() => {
                    return performDevicesOperationSuccessAction({
                        type: NOTIFICATION_TYPES[1],
                        message: i18n.t('ServiceScreen.hints.operationSuccessful'),
                    });
                }),
                catchError((err: AxiosError) => {
                    return of(performDevicesOperationErrorAction(parseError(err, ' ', true)));
                }),
                takeUntil(action$.pipe(ofType(ServiceActionType.EXIT_SERVICE_PAGE))),
            );
        }),
    );
};
